import React, { useMemo } from 'react';
import { Box, Typography, Chip } from '@mui/material';
import CollapsibleTable from '../../components/table';
import FilterComponent from '../../components/FilterComponent';
import ReportExportButton from '../../components/ReportExportButton';
import FileActionModal from '../../components/FileActionModal';
import FileUploadModal from '../../components/FileUploadModal';
import SimpleSuccessSpinner from '../../components/LoadingSpinner';

import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTesoreroSolicitudesController } from './hooks/useTesoreroSolicitudesController';

const formatCurrency = (val) => {
  const num = Number(val);
  if (isNaN(num)) return val || '$0.00';
  return num.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

export default function TesoreroSolicitudes() {
  const {
    data, loading, setLoading,
    openFileModal, setOpenFileModal, fileModalFiles,
    fileUploadModalOpen, setFileUploadModalOpen,
    adjuntos, setAdjuntos, selectedRow, setSelectedRow,
    empresaIdPref, usuarioId, tempFilterData, setTempFilterData, ejecutarBusquedaManual,
    handleViewFile, handleUploadAdjuntos, handleCustomZip, apiUrl
  } = useTesoreroSolicitudesController();

  const buttons = useMemo(() => [
    {
      id: 1,
      label: 'Ver Comprobantes y Archivos',
      icon: <VisibilityIcon />,
      buttonColor: '#0284c7',
      buttonFontColor: '#ffffff',
      isVisible: true,
      onClick: (row) => { setSelectedRow(row); handleViewFile(row); }
    },
    {
      id: 2,
      label: 'Subir Comprobante / Adjuntos',
      icon: <CloudUploadIcon />,
      buttonColor: '#10b981',
      buttonFontColor: '#ffffff',
      isVisible: true,
      onClick: (row) => { setSelectedRow(row); setFileUploadModalOpen(true); }
    },
  ], [handleViewFile, setSelectedRow, setFileUploadModalOpen]);

  const formattedData = useMemo(() => {
    const rawList = Array.isArray(data) ? data : (data?.contenido || []);
    const mapped = rawList.map((row) => ({
      ...row,
      TESORERO_ID_FOLIO_EMPRESA: '',
      TESORERO_SOLICITANTE_RFC: '',
      CENTRO_DEPTO: '',
      TESORERO_MONTO_ESTATUS: '',
      TESORERO_COMPROBANTE_REF: '',
    }));
    return Array.isArray(data) ? mapped : { ...data, contenido: mapped };
  }, [data]);

  const compactColumnOrderMemo = useMemo(() => [
    'TESORERO_ID_FOLIO_EMPRESA',
    'TESORERO_SOLICITANTE_RFC',
    'CENTRO_DEPTO',
    'TESORERO_MONTO_ESTATUS',
    'TESORERO_COMPROBANTE_REF',
    'FECHAPAGO',
  ], []);

  const compactColumnLabelsMemo = useMemo(() => ({
    TESORERO_ID_FOLIO_EMPRESA: 'Orden / ID',
    TESORERO_SOLICITANTE_RFC: 'Solicitante / RFC',
    CENTRO_DEPTO: 'Centro / Depto',
    TESORERO_MONTO_ESTATUS: 'Monto / Estatus',
    TESORERO_COMPROBANTE_REF: 'Comprobante',
    FECHAPAGO: 'Fecha Pago',
  }), []);

  const initialFiltersMemo = useMemo(() => ['FECHA_INICIO', 'FECHA_FIN', 'FOLIO', 'EMPRESA'], []);

  const hiddenFieldsMemo = useMemo(() => [
    'ID',
    'ID_SOLICITUD',
    'FOLIO',
    'FOLIO_OC',
    'MONTO',
    'RFC',
    'NOMBRE_EMPRESA',
    'EMPRESA',
    'EMPRESAID',
    'CENTROCOSTO',
    'CENTROCOSTOID',
    'NUMEROCC',
    'NUMERO_CC',
    'DEPARTAMENTO',
    'DEPARTAMENTOID',
    'SOLICITANTE',
    'METODOPAGO',
    'ESTATUS',
    'COMPROBANTE',
    'IMAGEN',
    'REFERENCIA',
    'HORA'
  ], []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 'clamp(6px, 0.8vw, 12px)', overflow: 'hidden', bgcolor: '#f8fafc' }}>
      <Box sx={{ flexShrink: 0, mb: 'clamp(6px, 0.8vw, 10px)' }}>
        <FilterComponent
          filterData={tempFilterData}
          setFilterData={setTempFilterData}
          handleClick={ejecutarBusquedaManual}
          handleEnterKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); ejecutarBusquedaManual(); } }}
          data={data}
          showFilterButton={false}
          showExportButton={false}
          showHeaderActions={true}
          initialFilters={initialFiltersMemo}
          persistEnabled
          persistKey="TesoreroSolicitudesFilters"
          persistVista="filters"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
        />
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', minHeight: 0 }}>
        <CollapsibleTable
          buttons={buttons}
          dataTable={formattedData}
          loading={loading}
          rightButtons={<ReportExportButton />}
          mobileCard={{ statusMode: 'none' }}
          persistEnabled
          persistKey="TesoreroSolicitudes_v3"
          persistVista="layout"
          usuarioId={usuarioId}
          empresaId={empresaIdPref}
          apiUrl={apiUrl}
          initialColumnOrder={compactColumnOrderMemo}
          columnLabels={compactColumnLabelsMemo}
          hiddenFields={hiddenFieldsMemo}
          stickyLeftKeys={['TESORERO_ID_FOLIO_EMPRESA']}
          density="small"
          compactActions
          maxInlineActions={2}
        />
      </Box>

      <FileActionModal
        open={openFileModal}
        onClose={() => setOpenFileModal(false)}
        files={fileModalFiles}
        onDownloadAll={handleCustomZip}
        showDownloadAll={true}
      />

      <FileUploadModal
        open={fileUploadModalOpen}
        onClose={() => { setFileUploadModalOpen(false); setAdjuntos([]); }}
        onFilesSelected={setAdjuntos}
        onSave={() => { if (selectedRow) handleUploadAdjuntos(selectedRow); }}
      />

      <SimpleSuccessSpinner open={loading} handleClose={() => setLoading(false)} message="Cargando archivos..." />
    </Box>
  );
}